/**
 * Materials — the vocabulary of selectable hue-anchor sets.
 *
 * Authorised by: design-direction §2 (the DECIDED direction is "Drystone structure, Waymark
 * discipline", where **Drystone owns *surface* (colour, texture, tone)** and Waymark owns
 * structure), §2 objection 2 (app-store presence is "a genuine commercial concern", resolved
 * by letting the accent run bolder), §3 (one base palette plus one focus accent), §7 (the
 * exact accent colour is explicitly "open for the team"), and product-concept §12 as amended
 * by the founder decision recorded in `docs/DEVIATIONS.md` M11.
 *
 * READ M11 BEFORE CHANGING ANYTHING IN THIS FILE. It is the entry that reconciles a paid
 * material with §12's "Sensory and accessibility settings are free forever, categorically",
 * and the reconciliation is load-bearing rather than rhetorical. The short version, because
 * the shape of this file is a direct consequence of it:
 *
 *   A material changes **hue and chroma only**. Every ramp in `@carn/tokens`' material table
 *   is solved to the *exact WCAG relative luminance* of the corresponding Drystone stop, and
 *   every colour that carries a contrast obligation is then re-solved by `theme.ts` from the
 *   ratio it owes. So a material cannot change text contrast, boundary contrast, touch-target
 *   size, motion, haptics, celebration, density, reading preferences, or the Intensity dial —
 *   it cannot reach any of them. The *sensory* controls are the dial and its seven channels,
 *   and every one of them stays free, at every level, for every material, including the
 *   near-monochrome reference rendering at colour level 0.
 *
 *   What Plus buys is therefore identity, not accommodation: no sensory outcome is reachable
 *   only by paying, because the free Drystone material spans the whole dial by itself.
 *
 * THE STANDING CONDITIONS M11 ATTACHES, both enforced in code rather than by memory:
 *
 *   1. **Drystone must never be degraded to make a paid material look better.** It is the
 *      reference rendering for the whole design system (design-direction §3 objection 2) and
 *      `packages/tokens/test/materials.test.ts` asserts every Drystone token is byte-identical
 *      to the pre-materials theme. Sabotaging the free tier is worse than pricing it, and it
 *      is the exact "ND tax" §12 names as the thing this audience is "justifiably hostile to".
 *   2. **A locked material is previewable in full, indefinitely, with no timer.** §12's ethics
 *      constraints forbid "fake-urgency discount timers to a population with impulse-control
 *      challenges"; a countdown on a preview is the same device wearing a different hat. See
 *      `materialPreviewable`.
 */

/**
 * The materials, in the fixed order they are offered. Drystone is first because it is the
 * free default and the reference rendering, not because it is the best.
 *
 * IDENTIFIERS, NOT COPY. Every user-visible word about a material — its name, its one-line
 * gloss, its description — lives in `packages/copy/src/catalogue.ts` under `material.*`, in
 * both registers, linted by the copy-law linter. A component maps an id to
 * `material.${id}.name` and asks `useCopy()` for the words (the same contract
 * `INTENSITY_CHANNELS` already works under).
 */
export const MATERIAL_IDS = [
  'drystone',
  'calch',
  'glo',
  'grug',
  'llanw',
  'ffion',
  'pride',
] as const;
export type MaterialId = (typeof MATERIAL_IDS)[number];

/** The free default, and the fallback whenever a paid choice is not currently entitled. */
export const DEFAULT_MATERIAL: MaterialId = 'drystone';

/**
 * Which side of the free/paid line each material sits on.
 *
 * `Record<MaterialId, Tier>` is a compile-time exhaustiveness check: a new material cannot be
 * added without someone stating, in this file, which tier it belongs to. That is deliberate —
 * M11's whole argument depends on the free tier keeping a material that spans the entire dial,
 * so "which tier" is not a detail to be discovered later at a call site.
 *
 * There is no rule here that Drystone must be the *only* free material. There is a rule that no
 * colour temperature is escapable only by paying, asserted in
 * `packages/tokens/test/materials.test.ts` (this package has no test that could: the property is
 * about pixels, and `@carn/core` has none). **Nothing reads this record to derive that** — it is
 * a hand-written literal, `@carn/core` declares zero dependencies, and importing `@carn/tokens`
 * here would be a cycle. The tokens test reads BOTH records and compares them; the dependency
 * runs one way only.
 *
 * `llanw: 'free'` — RULINGS R182 (CHANGED), amending `docs/DEVIATIONS.md` M11.
 * product-concept §11 lists "colour temperature" among the DECIDED "full sensory settings",
 * and `@carn/tokens`' `materials.ts` documents Llanw, in its own words, as the material for "a
 * user who finds Calch's warmth tiring" — with only Drystone free, that user had to pay to
 * escape a material that tired them, which is not honest. Llanw moves to the free side; four
 * materials remain behind Plus.
 *
 * R182 words the resulting property as "the free tier owns both a warm and a cool material".
 * Measured against the literal page ground rather than against the prose, it does not: Drystone's
 * ground is a near-neutral putty below the chroma floor, and every material's DARK ground is a
 * near-black with no temperature at all — so the free set is `{neutral, cool}` in light and
 * `{neutral}` in dark, and warm is Calch and Ffion, both paid. See
 * `packages/tokens/src/temperature.ts` for the numbers and `docs/OPEN-QUESTIONS.md` ("The free
 * tier owns no warm material") for the ruling that gap is waiting on. R182's *reason* — "no user
 * has to pay to escape a material that tires them" — does hold, and is what the test asserts.
 *
 * `pride: 'free'` — RULINGS R196, a founder decision. It is free for a reason that is about this
 * material specifically rather than about the tier maths: a material that exists to let a user say
 * something about themselves, sold only to people who can pay, says the wrong thing. M11's
 * argument is untouched either way — it constrains what may be PAID, never what may be free — and
 * the free set keeps a material that spans the whole dial regardless.
 *
 * IT IS APPENDED RATHER THAN PLACED. design-direction §4 fixes the spatial grammar and forbids
 * rearrangement, so a new material goes on the end and nothing above it moves. That does make the
 * free/paid interleave one step worse (free · paid · paid · paid · free · paid · **free**), which
 * is the subject of an existing ordering question in `docs/OPEN-QUESTIONS.md` — this entry is
 * named there rather than pre-empting it.
 */
export const MATERIAL_TIER: Record<MaterialId, 'free' | 'plus'> = {
  drystone: 'free',
  calch: 'plus',
  glo: 'plus',
  grug: 'plus',
  llanw: 'free',
  ffion: 'plus',
  pride: 'free',
};

export function isMaterialId(value: unknown): value is MaterialId {
  return typeof value === 'string' && (MATERIAL_IDS as readonly string[]).includes(value);
}

/**
 * Clamp any stored or synced value back to a renderable material.
 *
 * The counterpart of `normaliseIntensity`, for the same stated reason: a value arriving from
 * another device or a future app version must never produce an unrenderable theme. An unknown
 * id resolves to the default rather than throwing — a phone that syncs a material it has never
 * heard of should render Drystone, not fail to draw.
 */
export function normaliseMaterial(input: unknown): MaterialId {
  return isMaterialId(input) ? input : DEFAULT_MATERIAL;
}

/**
 * THE ONE PLACE A MATERIAL CHOICE MEETS AN ENTITLEMENT.
 *
 * Note what this function does NOT do: it never rewrites the user's stored choice. A lapsed
 * subscriber keeps `Settings.material` exactly as they left it and renders Drystone; the day
 * they resubscribe, their material comes back without them having to find it again. Overwriting
 * the stored value on lapse would be a second, silent punishment for a card expiring, and
 * "nothing is ever taken away" (CLAUDE.md) is the instinct this follows even though that law is
 * written about history rather than about settings.
 *
 * Pure and total, and deliberately takes a `Tier` rather than a `PlusStatus`, so the caller has
 * already been through `resolveTier` and there is exactly one definition of "is this user a
 * payer" in the codebase.
 */
export function effectiveMaterial(chosen: MaterialId, tier: 'free' | 'plus'): MaterialId {
  if (tier === 'plus') return chosen;
  return MATERIAL_TIER[chosen] === 'free' ? chosen : DEFAULT_MATERIAL;
}

/**
 * Whether a material may be shown to this user as a live, whole-app preview.
 *
 * Always true — and it is a function rather than a constant so that the call site reads as a
 * decision that was made, and so the one plausible future "improvement" (a time-limited trial)
 * has to come through here and meet M11's second standing condition and §12's ethics
 * constraints on its way. A preview has no clock, no counter, and no dismissal deadline.
 */
export function materialPreviewable(_material: MaterialId): boolean {
  return true;
}
